import type { z } from 'zod';

import { NO_POS, type SourcePos } from './ast.ts';
import { builtins } from './builtins/index.ts';
import { defaultEvalCtx, type EvalContext } from './EvalContext.ts';
import { createRootEnv, type Env, type EvalResult, lookup, resolveSymbolWithSessionFallthrough } from './env.ts';
import {
  AggregateExprError,
  EmptyScriptError,
  ExprError,
  LambdaCallArityError,
  liftKwargs,
  OpExecutionError,
  parseOpCall,
  ShapeError,
  TypeMismatchError,
  throwErrors,
  UnknownOpError,
  zodIssuesToErrors,
} from './errors.ts';
import { materialize, materializeIfLazy } from './materialize.ts';
import {
  type AstInput,
  astShape,
  foldAst,
  nodePos,
  normalize,
  type OpCall,
  opCallPos,
  type Spans,
} from './normalize.ts';
import {
  type ColumnRefInArg,
  createOpTable,
  type DatasetCapability,
  type NameTable,
  type OpMeta,
  opMeta,
  resolveOpCall,
  type SpecialCtx,
} from './op-meta.ts';
import { parse } from './parser.ts';
import { elemPath, kwargPath, mapValuePath, paramPath } from './path.ts';
import { inlineSetRefs } from './providers/set-refs.ts';
import { compileExpr } from './runtime/arquero.ts';
import { typeCheckScript } from './schema/check.ts';
import { runPropagateSchema } from './schema/infer.ts';
import { refineSchema } from './schema/refine.ts';
import { EMPTY_IDENTITY_TABLE, type IdentityTable } from './schema/row-identity.ts';
import { nestedDatasetCol, type Schema, schemaFromEntries } from './schema/types.ts';
import {
  type Cell,
  type Columns,
  Dataset,
  describeValueKind,
  expectMaterialized,
  isLambda,
  isLazy,
  Keyword,
  type LambdaValue,
  LazyDataset,
  MapValue,
  MaterializedDataset,
  type Value,
} from './value.ts';

/** Default op table — all builtin schemas. Consumers extend via `createOpTable`. */
export const defaultOpTable: NameTable = createOpTable([...builtins]);

/**
 * Per-evaluation invariants bundled into a single value, threaded
 * through every internal helper (`evalNode`, `evalOpCall`,
 * `dispatchCall`, `resolveEagerArgs`). Bundling avoids propagating a
 * `(spans, ops, evalCtx)` parameter list through every internal
 * signature; mirrors `BaseSpecialCtx` / `BaseOpMeta` in `op-meta.ts`.
 *
 * Public entry points (`evalExpr`, `evalScript`) keep their
 * argument-style ergonomics; internals receive the bundle.
 */
export type EvalState = {
  readonly spans: Spans;
  readonly ops: NameTable;
  /**
   * Composed view over provider-contributed row-identity contracts,
   * sourced from `EvalContext.identities` (falling back to
   * `EMPTY_IDENTITY_TABLE`). Threaded alongside `ops` into every internal
   * helper so the compile pass (`compileExpr`) and schema propagation
   * (`runPropagateSchema`) share one identity surface. An empty table
   * claims nothing — a benign no-op.
   */
  readonly identities: IdentityTable;
  readonly evalCtx: EvalContext;
  /**
   * The OUTER column name currently being lifted per-cell, set by
   * `liftPerCell` on the recursive `evalOpCall` so the re-entered
   * op `apply` / `propagateSchema` detects the per-cell form and
   * names per-axis outputs. `undefined` at the top level and for
   * row-level dispatch.
   */
  readonly liftColumn?: string;
};

/**
 * Parse an s-expression script and evaluate it against the given op
 * table. Returns the value of the last top-level form. Bindings introduced
 * by `dataset` flow into subsequent forms. The `ctx` defaults to a fresh
 * in-memory `EvalContext` — production callers supply one constructed from
 * React state (via `useEvalExpr()`) so session bindings and memoized I/O
 * persist across calls.
 */
export const evalExpr = async (
  src: string,
  ctx: EvalContext = defaultEvalCtx(),
  ops: NameTable = defaultOpTable,
): Promise<Value> => {
  const { ast, spans } = normalize(parse(src));
  return evalScript(ast, spans, ctx, ops);
};

/**
 * Pure recursive walker — collects the source positions of every
 * misplaced `(def …)` call in an AST subtree. Module-level so the
 * function value is allocated once and reused across `evalScript`
 * invocations rather than re-closed per call. `spans` is threaded
 * explicitly through the recursion (mirroring `paramPath` / `path`)
 * rather than captured.
 *
 * The caller checks the returned position list and throws once at
 * the boundary, so the walker itself is side-effect-free.
 */
const findMisplacedDefs = (node: AstInput, path: string, isTopLevel: boolean, spans: Spans): readonly SourcePos[] => {
  const shape = astShape(node);
  if (shape.kind === 'opCall') {
    const here = shape.call.name === 'def' && !isTopLevel ? [opCallPos(spans, path)] : [];
    const positional = shape.call.params ?? [];
    const fromParams = positional.flatMap((child, i) =>
      findMisplacedDefs(child as AstInput, paramPath(path, i), false, spans),
    );
    const fromKwargs = Object.entries(shape.call)
      .filter(([k]) => k !== 'name' && k !== 'params')
      .flatMap(([k, v]) => findMisplacedDefs(v as AstInput, kwargPath(path, k), false, spans));
    return [...here, ...fromParams, ...fromKwargs];
  }
  if (shape.kind === 'vector') {
    return shape.elements.flatMap((el, i) => findMisplacedDefs(el, elemPath(path, i), false, spans));
  }
  return [];
};

/**
 * Evaluate a pre-normalized script (JS-object AST plus spans sidecar).
 * Direct callers can construct `ast` programmatically; `spans` may be empty
 * when the input has no source attribution.
 *
 * Before evaluation, the script is fed through the pre-evaluation
 * type-check pass (`schema/check.ts`). Any collected
 * diagnostics short-circuit evaluation — they're wrapped in
 * `AggregateExprError` and thrown before any op's `apply` runs, so
 * static type errors surface in one LLM round-trip without paying
 * for I/O.
 *
 * `ctx` defaults to a fresh in-memory `EvalContext` so test callers
 * stay terse; production callers pass an explicit one.
 */
export const evalScript = async (
  ast: readonly AstInput[],
  spans: Spans,
  ctx: EvalContext = defaultEvalCtx(),
  ops: NameTable = defaultOpTable,
  initialEnv?: Env,
): Promise<Value> => {
  if (ast.length === 0) {
    throw new EmptyScriptError();
  }
  // Structural check: `(def …)` may only appear at the script root.
  // `findMisplacedDefs` (module-level) collects misplaced positions
  // via pure recursion; we throw once at the boundary. Surfaces
  // ahead of the schema-level typeCheckScript so a misplaced def
  // gets a precise positional error.
  const misplacedDefs = ast.flatMap((form, i) => findMisplacedDefs(form, elemPath('', i), true, spans));
  if (misplacedDefs.length > 0) {
    throw new ShapeError('(def …) is only valid at the script top level', misplacedDefs[0] as SourcePos);
  }
  // Surface session-bound dataset schemas to the type-check pass.
  // `BindingResolver.resolve` is async (transcript replay) but
  // `typeCheckScript` is sync — so we pre-resolve recipes upfront and
  // hand the pass a sync `name → Schema` lookup. Resolve only the
  // recipes whose name actually appears in the script (collected via a
  // free-symbol walk over the AST): production transcript resolvers
  // can be I/O-heavy, so resolving every in-scope recipe regardless of
  // reference would regress prod throughput on sessions with many
  // unreferenced bindings. Memoization inside `BindingResolver.resolve`
  // ensures the runtime resolution that follows hits cache; individual
  // recipe failures are swallowed so they don't mask the type errors
  // the pass is meant to surface (the runtime will surface the
  // failure when the script actually references that binding).
  const referencedNames = new Set<string>();
  const collectSymbols = (node: unknown): void => {
    if (typeof node !== 'object' || node === null) {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(collectSymbols);
      return;
    }
    const obj = node as Record<string, unknown>;
    if (typeof obj.$sym === 'string') {
      referencedNames.add(obj.$sym);
      return;
    }
    // OpCall + any other object shape: recurse over values. Parser
    // produces UNLIFTED OpCalls (`:k v` pairs sit inside `params`), so
    // `params` already covers what'd become kwargs after the lift.
    Object.values(obj).forEach(collectSymbols);
  };
  ast.forEach(collectSymbols);
  const sessionSchemaEntries = await Promise.all(
    ctx.bindings.recipes
      .filter((recipe) => referencedNames.has(recipe.name))
      .map(async (recipe): Promise<readonly [string, Schema] | undefined> => {
        try {
          const value = await ctx.bindings.resolve(recipe.name);
          return value instanceof Dataset ? [recipe.name, value.schema] : undefined;
        } catch {
          return undefined;
        }
      }),
  );
  const sessionSchemas = new Map(sessionSchemaEntries.filter((e): e is readonly [string, Schema] => e !== undefined));
  const resolveSessionSchema = (name: string): Schema | undefined => sessionSchemas.get(name);
  const identities = ctx.identities ?? EMPTY_IDENTITY_TABLE;
  const typeErrors = typeCheckScript({
    script: ast,
    ops,
    spans,
    getProvider: ctx.getProvider,
    identities,
    resolveSessionSchema,
  });
  if (typeErrors.length > 0) {
    throwErrors(typeErrors);
  }
  const opNames = new Set(ops.keys());
  const startEnv = initialEnv ?? createRootEnv(opNames);
  const state: EvalState = { spans, ops, identities, evalCtx: ctx };
  const seq = await ast.reduce(
    async (acc, node, i) => {
      const prev = await acc;
      const formPath = elemPath('', i);
      // Inline every cross-dataset set reference in this form to a literal
      // before evaluation: a named foreign `(col other-ds …)` column or an
      // inline `(query …)` sub-query operand is resolved ONCE against the
      // threaded env + session bindings, so no `Dataset`-valued node or
      // foreign ref survives into any op's apply, partition, or replay node.
      // Per-form (not once at the root) so the scope predicate sees prior
      // `(def …)` datasets bound into `env` as the reduce threads it.
      const lowered = await inlineSetRefs(node, {
        evalNode: (n) => evalNode(n, prev.env, formPath, state).then((r) => r.value),
        materialize: (ds) => materializeIfLazy(ds, ctx, ops),
        isBoundDataset: (name) => lookup(prev.env, name) instanceof Dataset || ctx.bindings.has(name),
        getProvider: ctx.getProvider,
        session: ctx.session,
        identities,
        pos: nodePos(spans, formPath),
      });
      return evalNode(lowered, prev.env, formPath, state);
    },
    Promise.resolve<EvalResult>({ value: null, env: startEnv }),
  );
  return seq.value;
};

/**
 * Recursive evaluation of a single AST node. Dispatch via `astShape`:
 *
 * - Embedded values (primitive / Date / Dataset / opaque object) → return
 *   the value as-is.
 * - Vectors → recursively evaluate each element; produce an array Value.
 * - `{ $sym }` → lookup in env; unbound → `UnboundSymbolError`.
 * - `{ name, ... }` → op call; validate against schema; dispatch eager/special.
 */
export const evalNode = async (node: AstInput, env: Env, path: string, state: EvalState): Promise<EvalResult> =>
  foldAst<Promise<EvalResult>>(node, {
    embedded: async (shape) => ({ value: shape.value, env }),
    vector: async (shape) => {
      const settled = await Promise.allSettled(
        shape.elements.map((el, i) => evalNode(el, env, elemPath(path, i), state).then((r) => r.value)),
      );
      const { ok, errors } = partitionSettled(settled);
      if (errors.length > 0) {
        throwErrors(errors);
      }
      return { value: ok, env };
    },
    keyword: async (shape) => ({ value: new Keyword(shape.name), env }),
    map: async (shape) => {
      const settled = await Promise.allSettled(
        shape.entries.map((entry, i) =>
          evalNode(entry.v, env, mapValuePath(path, entry.k, i), state).then((r) => [entry.k, r.value] as const),
        ),
      );
      const { ok, errors } = partitionSettled(settled);
      if (errors.length > 0) {
        throwErrors(errors);
      }
      return { value: new MapValue(new Map(ok)), env };
    },
    symbolRef: async (shape) => {
      // Env → binding-scope fallthrough; see
      // `resolveSymbolWithSessionFallthrough` for the lookup semantics
      // (a cross-turn miss re-derives the producer's value lazily — async,
      // so this is awaited).
      const value = await resolveSymbolWithSessionFallthrough(
        env,
        state.evalCtx.bindings,
        shape.sym,
        nodePos(state.spans, path),
      );
      return { value, env };
    },
    opCall: (shape) => evalOpCall(shape.call, env, path, state),
  });

/**
 * Shared 5-step dispatch: lookup → meta-get → eager-or-special branch →
 * apply → wrap non-`ExprError` throws as `OpExecutionError`. Used by
 * `evalOpCall`'s row-level standard branch AND by the lazy/per-cell
 * wrapper's fall-through paths.
 */
const dispatchCall = async (
  call: OpCall,
  schema: z.ZodObject,
  meta: OpMeta,
  env: Env,
  path: string,
  state: EvalState,
): Promise<EvalResult> => {
  // Kwargs are already call by `evalOpCall` (the single dispatch entry
  // point) — `dispatchCall` consumes the call call directly. This
  // keeps a single source of truth for the lift; `propagateSchema`,
  // `referencedColumns`, and `apply` all see the same shape.
  const buildCtx = (): SpecialCtx => ({
    env,
    path,
    spans: state.spans,
    evalCtx: state.evalCtx,
    ops: state.ops,
    identities: state.identities,
    liftColumn: state.liftColumn,
    evalNode: (n, e, p) => evalNode(n, e, p, state),
    compile: (node, p) => compileExpr(node as AstInput, state.ops, state.identities, state.spans, p, env),
    materialize: (ds) => materialize(ds, state.evalCtx, state.ops),
  });
  try {
    if (meta.evaluation === 'special') {
      const parsed = parseOpCall(schema, call);
      if (!parsed.success) {
        throwErrors(zodIssuesToErrors(parsed.error, path, state.spans));
      }
      return (await meta.apply(parsed.data as never, buildCtx())) as EvalResult;
    }
    const parsedData = await resolveEagerArgs(call, schema, env, path, state);
    const value = (await meta.apply(parsedData as never, buildCtx())) as Value;
    return { value, env };
  } catch (e) {
    if (e instanceof ExprError) {
      throw e;
    }
    throw new OpExecutionError(call.name, e instanceof Error ? e.message : String(e), opCallPos(state.spans, path));
  }
};

/**
 * Dispatch a top-level op call. Three-branch wrapper for dataset-
 * consuming TRANSFORM ops (those declaring `propagateSchema`):
 *
 *   1. **Lazy** — first positional evaluates to a `LazyDataset`: append
 *      a `PlanNode` to the plan (capturing the active `liftColumn` so a
 *      deferred per-cell op re-enters its per-cell form at materialise
 *      time), propagate the schema once via `runPropagateSchema`, return
 *      a new `LazyDataset`. The op's `apply` is never invoked; it runs
 *      later, at materialisation time.
 *   2. **Per-cell** — first positional is `MaterializedDataset` and at
 *      least one ref (from `meta.referencedColumns`) points at a column
 *      whose `ColumnType` is `nested-dataset`. Each cell is itself a
 *      `Dataset`; we apply the op to each cell via a recursive
 *      `evalOpCall` (lazy cells stay lazy, materialised cells execute).
 *      The outer column's schema entry rewrites to `nested-dataset`
 *      wrapping the op's `propagateSchema` output against the inner
 *      schema.
 *   3. **Materialised, no lift** — standard arquero execution via
 *      `dispatchCall`. The propagated schema (from `runPropagateSchema`)
 *      is overlaid on apply's all-`unknown` arquero output.
 *
 * Lambda interception: if the call's head is a lambda binding in env,
 * this is a lambda invocation, not an op dispatch. Non-transform ops
 * (scalar funcs, top-level forms, source ops with `sourceSchema` but no
 * `propagateSchema`, etc.) go straight to `dispatchCall`.
 */
export const evalOpCall = async (call: OpCall, env: Env, path: string, state: EvalState): Promise<EvalResult> => {
  // Lambda interception. If the call's head resolves in env (lexical
  // binding) to a `LambdaValue`, this is a lambda invocation, not an
  // op dispatch. The env lookup is sync + cheap (one `Map.get` per op
  // call); a registered op name can never appear in env because
  // `checkBindable` rejects shadowing via `ShadowingOpError` at bind
  // time. The miss case (the overwhelming majority) falls through to
  // the standard dispatch path with zero added overhead beyond
  // the Map lookup.
  const headBound = env.bindings.has(call.name) ? env.bindings.get(call.name) : undefined;
  if (headBound !== undefined && isLambda(headBound)) {
    return callLambda(headBound, call, env, path, state);
  }
  const { schema, meta } = resolveOpCall(
    call.name,
    state.ops,
    opMeta,
    opCallPos(state.spans, path),
    'op',
    UnknownOpError,
  );
  const op = meta as OpMeta;
  // Schema-directed kwarg lift happens here — once, at the dispatch
  // entry point — so propagateSchema, referencedColumns, resolveSetRefs,
  // and apply all see the OpCall in the same lifted shape (declared
  // kwargs as top-level fields, positionals in `params`). Lifting later
  // in `dispatchCall` would leave the dataset-branch propagator reading
  // pre-lift `params` and miss kwargs entirely.
  const lifted = liftKwargs(schema, call) as OpCall;
  // Wrapper engagement is gated on `propagateSchema` specifically — the
  // transform half of `DatasetCapability`. Source ops declare `dataset`
  // for static-check participation only (their `sourceSchema` hook lets
  // the type-check pass validate `:filter` / `:columns` refs against
  // the view schema) and dispatch through the standard path, since
  // they don't take a Dataset positional and don't have an input
  // schema to propagate.
  if (op.dataset?.propagateSchema === undefined) {
    return dispatchCall(lifted, schema, op, env, path, state);
  }
  return evalDatasetOp(lifted, schema, op, env, path, state);
};
/**
 * Three-branch wrapper for dataset-consuming ops. Resolves the dataset
 * arg (first positional) once, then routes to:
 *
 *   - **Lazy plan-node append** when the dataset is `LazyDataset`. The
 *     op's args (with `params[0]` stripped — the replay loop re-prepends
 *     the threaded dataset) and propagated schema are captured on a new
 *     `PlanNode`.
 *   - **Per-cell lift** when refs point into a `nested-dataset` column.
 *     `liftPerCell` recursively re-enters `evalOpCall` per cell.
 *   - **Materialised, no lift** — dispatch through the standard path,
 *     overlaying the propagated schema on apply's arquero-derived output.
 *
 * Cross-dataset `(col other-ds …)` set refs and inline `(query …)`
 * sub-query operands in this op's args were already inlined to literal
 * vectors by `inlineSetRefs` (run per-form in `evalScript` before any op
 * dispatch), so the call this wrapper receives carries only literals.
 *
 * For ops whose output is data-dependent (runtime-width: `pivot`,
 * `spread`) `propagateSchema` may return `undefined`. In that case the
 * lazy branch force-materialises and re-dispatches so `apply` can build
 * the schema from concrete data.
 */
const evalDatasetOp = async (
  call: OpCall,
  schema: z.ZodObject,
  op: OpMeta,
  env: Env,
  path: string,
  state: EvalState,
): Promise<EvalResult> => {
  const dsAst = call.params?.[0];
  if (dsAst === undefined) {
    throw new ShapeError(`(${call.name} <Dataset> …) expects a Dataset as its first arg`, opCallPos(state.spans, path));
  }
  const { value: dsValue } = await evalNode(dsAst as AstInput, env, paramPath(path, 0), state);
  if (!(dsValue instanceof Dataset)) {
    throw new TypeMismatchError(
      `(${call.name} <Dataset> …)`,
      'Dataset',
      describeValueKind(dsValue),
      opCallPos(state.spans, path),
    );
  }
  // Precondition: evalOpCall routes to evalDatasetOp iff `op.dataset`
  // is declared. The non-null assertion is the dispatch contract; a
  // missing namespace here is a programmer bug.
  const datasetCap = op.dataset as DatasetCapability;
  if (isLazy(dsValue)) {
    const newSchema = runPropagateSchema(
      datasetCap,
      call,
      dsValue.schema,
      state.ops,
      state.identities,
      state.spans,
      path,
      state.liftColumn,
    );
    if (newSchema === undefined) {
      // Runtime-width op: force the input and re-dispatch so apply
      // builds the schema on concrete data.
      const forced = await materialize(dsValue, state.evalCtx, state.ops);
      const rewritten: OpCall = {
        ...call,
        params: [forced, ...(call.params ?? []).slice(1)],
      };
      return evalDatasetOp(rewritten, schema, op, env, path, state);
    }
    const tailArgs: OpCall = {
      ...call,
      params: (call.params ?? []).slice(1),
    };
    const lazy = new LazyDataset(
      {
        source: dsValue.plan.source,
        ops: [
          ...dsValue.plan.ops,
          { op: call.name, args: tailArgs, schemaAfter: newSchema, liftColumn: state.liftColumn },
        ],
      },
      newSchema,
    );
    return { value: lazy, env };
  }
  const materialized = expectMaterialized(dsValue, `(${call.name} <Dataset> …)`, opCallPos(state.spans, path));
  const refs = (datasetCap.referencedColumns?.(call as never, path) ?? []) as readonly ColumnRefInArg[];
  // Lift axes = nested-dataset columns referenced *via row-scope reads*.
  // A row-scope read is unambiguously identified by the `(col …)` AST
  // shape — its `colLabels` lists the binding-prefix + column + struct
  // sub-path. Bare-name refs (no `colLabels`) are positional col LABELS
  // (e.g. `time-bucket`'s cols arg, `:group-by` keys, agg-pair input
  // cols) — they name a column, they don't read it per-row, so they
  // must NOT trigger auto-lift. Ops that need per-cell iteration over
  // such positionally-named columns manage it themselves in their
  // `apply` via `liftPerCellOnColumns`. The schema-check pass still
  // validates bare-name refs for `UnknownColumnRefError`.
  const liftAxes = [
    ...new Set(
      refs.flatMap((r) => {
        if (r.colLabels === undefined) {
          return [];
        }
        const nestedLabel = r.colLabels.find((l) => materialized.schema.get(l)?.kind === 'nested-dataset');
        return nestedLabel === undefined ? [] : [nestedLabel];
      }),
    ),
  ];
  if (liftAxes.length > 0) {
    // Auto-lift pre-check: each `(col …)` ref that triggered the lift
    // on an axis must address some column that EXISTS on the cell's
    // inner schema (other than the axis itself, which names the
    // nested-dataset column at the OUTER level and is stripped going
    // inwards). Without this gate, a predicate like
    // `(filter ds #(is-not-null (col self X)))` against a nested-dataset
    // `X` would auto-lift per cell and then fail deep inside the cell
    // predicate's evaluator with a cryptic "unknown column: X" against
    // the cell's `{timestamp, value, …}` schema. Surfacing it here
    // gives a precise diagnostic that explains the lift.
    const liftIssues = liftAxes.flatMap((axis) => {
      const axisType = materialized.schema.get(axis);
      if (axisType?.kind !== 'nested-dataset') {
        return [];
      }
      const innerSchema = axisType.innerSchema;
      const offending = refs.filter((r) => {
        if (r.colLabels === undefined || !r.colLabels.includes(axis)) {
          return false;
        }
        const others = r.colLabels.filter((l) => l !== axis);
        return !others.some((l) => innerSchema.has(l));
      });
      return offending.length === 0 ? [] : [{ axis, innerSchema, refs: offending } as const];
    });
    if (liftIssues.length > 0) {
      const first = liftIssues[0] as {
        readonly axis: string;
        readonly innerSchema: Schema;
        readonly refs: readonly ColumnRefInArg[];
      };
      const refList = first.refs.map((r) => `\`${r.name}\``).join(', ');
      const innerCols = Array.from(first.innerSchema.keys()).join(', ');
      throw new ShapeError(
        `${call.name} was auto-lifted per-cell on nested-dataset column \`${first.axis}\`. Reference(s) ${refList} name the outer column with no inner-schema label — ${call.name}'s body runs row-by-row against the cell's inner schema {${innerCols}}, so the ref cannot resolve. Address an inner column directly, or reduce the cell to a row-level scalar upstream before referencing it here.`,
        opCallPos(state.spans, path),
      );
    }
    const lifted = await liftPerCell(call, materialized, liftAxes, op, env, path, state);
    return { value: lifted, env };
  }
  // Substitute the resolved materialised dataset into params[0] so apply
  // receives the materialised value directly (avoids resolveDsArg
  // re-materialisation work inside apply).
  const finalCall: OpCall = {
    ...call,
    params: [materialized, ...(call.params ?? []).slice(1)],
  };
  const result = await dispatchCall(finalCall, schema, op, env, path, state);
  const propagated = runPropagateSchema(
    datasetCap,
    call,
    materialized.schema,
    state.ops,
    state.identities,
    state.spans,
    path,
    state.liftColumn,
  );
  if (!(result.value instanceof MaterializedDataset)) {
    return result;
  }
  // Re-wrap overlays a schema reconciled against the actual cells
  // (`refineSchema`: fill `unknown`s, derive numeric column units from cell
  // evidence). A runtime-width op (`pivot`, `spread`, zip-form `unnest`)
  // returns `undefined` from `propagateSchema` and builds its own schema in
  // `apply`; reconcile THAT against the cells so cell-level units surface on
  // its output too. The op's apply already computed the correct identity
  // axis on `result.value`, so carry it through verbatim.
  return {
    value: new MaterializedDataset(
      result.value.columns,
      refineSchema(propagated ?? result.value.schema, result.value.columns),
      result.value.identity,
    ),
    env: result.env,
  };
};

/**
 * Invoke a `LambdaValue` with the call's positional args. The lambda
 * captured its `closureEnv` at definition; each invocation extends that
 * env with one new frame binding `params → arg-values`. Args are evaluated
 * eagerly in the CALLER's env (where the arg expressions are written) —
 * the body's references to its own params then resolve through the new
 * frame in `closureEnv`. The body is evaluated in the new frame; its
 * `EvalResult.value` is the lambda's return.
 *
 * Closure environment, NOT caller environment: a name referenced from
 * the body resolves against `closureEnv` (extended with the call's
 * params), not against the caller's env. This is closure semantics —
 * a lambda's free variables bind to the values visible where the
 * lambda was created.
 *
 * Arity check is a single equality. Variadic lambdas (`& rest`) are not
 * supported in this iteration; a wrong-count call raises
 * `LambdaCallArityError` at the call site.
 */
const callLambda = async (
  lambda: LambdaValue,
  call: OpCall,
  callerEnv: Env,
  path: string,
  state: EvalState,
): Promise<EvalResult> => {
  const argNodes = call.params ?? [];
  if (argNodes.length !== lambda.params.length) {
    throw new LambdaCallArityError(lambda.params.length, argNodes.length, opCallPos(state.spans, path));
  }
  // Evaluate args eagerly in the caller's env, in parallel.
  const argValues = await Promise.all(
    argNodes.map((arg, i) => evalNode(arg as AstInput, callerEnv, paramPath(path, i), state).then((r) => r.value)),
  );
  const paramBindings: readonly [string, Value][] = lambda.params.map((paramName, i): [string, Value] => [
    paramName,
    argValues[i] as Value,
  ]);
  const frame: Env = {
    bindings: new Map<string, Value>([...lambda.closureEnv.bindings, ...paramBindings]),
    parent: lambda.closureEnv.parent,
    opNames: lambda.closureEnv.opNames,
  };
  const { value } = await evalNode(lambda.body, frame, path, state);
  // The lambda's own EVAL extends `frame`, not the caller's env, so we
  // restore `callerEnv` for the dispatcher — calling a lambda is an
  // expression, not a binding form.
  return { value, env: callerEnv };
};

/**
 * Resolve an eager op/verb call's args (positional + kwargs) in parallel,
 * aggregate any errors, then schema-validate the assembled shape. Returns
 * the parsed data on success; throws an `ExprError` (single leaf or
 * `AggregateExprError`) on failure. Shared between `evalOpCall` and
 * `evalVerbCall` (both routed through `dispatchCall`) so eager arg
 * semantics live in one place.
 */
const resolveEagerArgs = async (
  call: OpCall,
  schema: z.ZodObject,
  env: Env,
  path: string,
  state: EvalState,
): Promise<unknown> => {
  const positional = call.params ?? [];
  const kwargEntries = Object.entries(call).filter(([k]) => k !== 'name' && k !== 'params');

  const positionalSettled = await Promise.allSettled(
    positional.map((arg, i) => evalNode(arg, env, paramPath(path, i), state).then((r) => r.value)),
  );
  const kwargsSettled = await Promise.allSettled(
    kwargEntries.map(([k, v]) =>
      evalNode(v as AstInput, env, kwargPath(path, k), state).then((r) => [k, r.value] as const),
    ),
  );

  const { ok: paramValues, errors: paramErrors } = partitionSettled(positionalSettled);
  const { ok: kwargValues, errors: kwargErrors } = partitionSettled(kwargsSettled);
  const errors = [...paramErrors, ...kwargErrors];
  if (errors.length > 0) {
    throwErrors(errors);
  }

  const resolved: Record<string, unknown> = {
    name: call.name,
    params: paramValues,
    ...Object.fromEntries(kwargValues),
  };
  const parsed = parseOpCall(schema, resolved);
  if (!parsed.success) {
    throwErrors(zodIssuesToErrors(parsed.error, path, state.spans));
  }
  return parsed.data;
};

/**
 * Per-cell lift helper (multi-axis). Called by `evalDatasetOp` when an
 * op's `referencedColumns` reports refs into one OR MORE `nested-dataset`
 * columns on the input dataset. For EACH such column the op's `apply`
 * is re-entered per cell-`Dataset` independently (via a recursive
 * `evalOpCall`, so lazy cells stay lazy and the op is never handed a
 * `LazyDataset`); the outer `MaterializedDataset` is then rebuilt with,
 * for every lifted column:
 *
 *   - `columns[col]` replaced by that column's per-cell results.
 *   - `schema[col]` rewritten to `nested-dataset` wrapping the op's
 *     `propagateSchema` output against the column's inner schema,
 *     computed with `liftColumn = col` so the op can name per-axis
 *     outputs (e.g. `time-bucket` emits `<col>-<agg>`).
 *
 * Single-axis is the n=1 case. Cross-axis bucket-grid alignment (the
 * union / null-fill) is NOT performed here: it is realised by
 * `time-bucket`'s deterministic `:window` grid (every axis emits the
 * same keys) and finalised by multi-column `unnest`'s zip — the only
 * consumer that needs the axes positionally aligned and the only place
 * a cell is legitimately materialised. Keeping this helper
 * materialisation-free preserves the substrate invariant "lazy cells
 * stay lazy through the recursion".
 *
 * Non-lifted columns pass through unchanged. The inner-schema-after
 * is the single source of truth for each lifted column's shape — not
 * derived from produced cells (a cell may be a `LazyDataset` whose
 * schema is already final), so zero-row outer datasets still rewrite
 * correctly.
 */
const liftPerCell = async (
  call: OpCall,
  dataset: MaterializedDataset,
  columnNames: readonly string[],
  op: OpMeta,
  env: Env,
  path: string,
  state: EvalState,
): Promise<MaterializedDataset> => {
  // Resolve each axis once: its inner-schema-after (SSOT for the
  // rewritten outer type, computed BEFORE iterating cells so it is
  // uniform even for zero-row outer datasets), and its per-cell results
  // from a recursive dispatch carrying `liftColumn = col`. Per-cell
  // apply substitutes the cell-Dataset into params[0] so the op sees
  // its own per-cell view as the threaded dataset.
  const lifted = await Promise.all(
    columnNames.map(
      async (columnName): Promise<readonly [string, { innerSchemaAfter: Schema; newCells: readonly Cell[] }]> => {
        const cells = dataset.columns[columnName];
        const colType = dataset.schema.get(columnName);
        if (cells === undefined || colType?.kind !== 'nested-dataset') {
          // Dispatch invariant — `evalDatasetOp` only selects axes whose
          // schema entry is nested-dataset. Defensive re-check.
          throw new OpExecutionError(
            call.name,
            `per-cell lift expected column \`${columnName}\` to be nested-dataset`,
            opCallPos(state.spans, path),
          );
        }
        // liftPerCell is only reached when evalDatasetOp has already
        // confirmed `op.dataset !== undefined`.
        const datasetCap = op.dataset as DatasetCapability;
        const innerSchemaAfter =
          runPropagateSchema(
            datasetCap,
            call,
            colType.innerSchema,
            state.ops,
            state.identities,
            state.spans,
            path,
            columnName,
          ) ?? colType.innerSchema;
        const axisState: EvalState = { ...state, liftColumn: columnName };
        // A timeseries-ref column legitimately carries a non-`Dataset`
        // cell (typically `null`) for an entity that has no datapoints —
        // qkg returns null there. Aborting the WHOLE op for one such row
        // is wrong: substitute an empty nested dataset (the column's
        // declared inner schema, zero rows) so the op's per-cell `apply`
        // runs on emptiness and the outer row is preserved.
        // An inner per-cell dataset is never instance-backed in its own
        // right — its identity axis is `undefined`.
        const emptyCell = (): MaterializedDataset =>
          new MaterializedDataset(
            Object.fromEntries(Array.from(colType.innerSchema.keys(), (k) => [k, [] as never[]])),
            colType.innerSchema,
            undefined,
          );
        const newCells = (await Promise.all(
          cells.map(async (cell): Promise<Cell> => {
            const cellDs = cell instanceof Dataset ? cell : emptyCell();
            const cellCall: OpCall = {
              ...call,
              params: [cellDs, ...(call.params ?? []).slice(1)],
            };
            const result = await evalOpCall(cellCall, env, path, axisState);
            return result.value as Cell;
          }),
        )) as readonly Cell[];
        return [columnName, { innerSchemaAfter, newCells }] as const;
      },
    ),
  );
  const liftedByName = new Map(lifted);
  // Rebuild the outer schema: every lifted column's type rewrites to
  // nested-dataset(<inner-after>); insertion order preserved.
  const newOuterSchema = schemaFromEntries(
    Array.from(dataset.schema.entries()).map(
      ([k, t]) =>
        (liftedByName.has(k)
          ? [k, nestedDatasetCol((liftedByName.get(k) as { innerSchemaAfter: Schema }).innerSchemaAfter)]
          : [k, t]) as readonly [string, typeof t],
    ),
  );
  const newColumns: Columns = Object.fromEntries(
    Array.from(dataset.schema.keys()).map((name) => [
      name,
      liftedByName.get(name)?.newCells ?? dataset.columns[name] ?? [],
    ]),
  );
  // The lift rewrites per-cell columns but preserves the OUTER row set and
  // order, so the outer identity axis passes through unchanged.
  return new MaterializedDataset(newColumns, newOuterSchema, dataset.identity);
};

/**
 * Collect `Promise.allSettled` results into successes + flat error list.
 * Nested aggregates are flattened so callers see a single flat
 * list of leaf errors regardless of how many branches errored.
 */
const partitionSettled = <T>(settled: readonly PromiseSettledResult<T>[]): { ok: T[]; errors: ExprError[] } => {
  const ok: T[] = [];
  const errors: ExprError[] = [];
  settled.forEach((r) => {
    if (r.status === 'fulfilled') {
      ok.push(r.value);
      return;
    }
    errors.push(...errorsFrom(r.reason));
  });
  return { ok, errors };
};

/**
 * Normalise a caught throwable to a flat list of `ExprError` leaves.
 * - `AggregateExprError` → flattened children
 * - `ExprError` → `[e]`
 * - anything else (rare — almost all throws are ExprError) → wrapped as a
 *   sentinel `OpExecutionError` so the partition can continue.
 */
const errorsFrom = (e: unknown): readonly ExprError[] => {
  if (e instanceof AggregateExprError) {
    return e.flatten();
  }
  if (e instanceof ExprError) {
    return [e];
  }
  return [new OpExecutionError('<unknown>', e instanceof Error ? e.message : String(e), NO_POS)];
};

// `zodIssuesToErrors` + `throwErrors` are hoisted to `errors.ts` as the
// engine SSOT for Zod-issue → ExprError + leaf-or-aggregate throw; both
// are imported above and reused by `compileAggPair` / future dispatch
// sites without having to copy the wiring.
